'use client';

import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { useForm, Controller } from "react-hook-form";
import { Pencil, AlertCircle, Building2, Globe } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLoader } from "../../_helper/LoaderContext";
import { toast } from "react-toastify";
import { useLab } from "../../_helper/PharmacyProvider";
import { updatUser, viewUser } from "./service/user-service";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { cityMaster, districtMaster, stateMaster } from "../LabProfile/service/profile-service";
import S3ImageLoader from "../../S3ImageLoader";
import errorProfile from "../../assets/images/blank_profile.png";

export default function EditUser() {
    const { id } = useParams();
    const [profileImage, setProfileImage] = useState(null);
    const [imageSource, setImageSource] = useState(errorProfile);
    const [profileImageFile, setProfileImageFile] = useState(null);
    const [imageError, setImageError] = useState("");
    const { currentLab } = useLab();
    const { setLoading } = useLoader();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        country: "IN",
        state: "",
        district: "",
        city: "",
        pincode: ""
    });

    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingLocation, setLoadingLocation] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        control,
        formState: { errors, isSubmitting },
    } = useForm();

    // Fetch user and prefill
    useEffect(() => {
        const fetchUser = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const res = await viewUser(id);
                if (res.data.success && res.data.data) {
                    const user = res.data.data;
                    const cp = user.contact_person || {};

                    // Profile image
                    if (cp.profile_picture || cp.profilePicture) {
                        setProfileImage(cp.profile_picture || cp.profilePicture);
                    }

                    // Basic fields
                    reset({
                        firstName: cp.first_name || "",
                        lastName: cp.last_name || "",
                        email: cp.contact_email || "",
                        mobile: cp.mobile || "",
                        address: cp.address || "",
                    });

                    // Country logic
                    const isIndia = cp.country === "India" || cp.country === "IN";
                    const countryCode = isIndia ? "IN" : "OTHER";

                    // Temporary hold values
                    const preValues = {
                        state: cp.state || "",
                        district: cp.district || "",
                        city: cp.city || "",
                        pincode: cp.pin_code || cp.pincode || ""
                    };

                    // Set country first
                    setFormData(prev => ({
                        ...prev,
                        country: countryCode,
                        state: "",
                        district: "",
                        city: "",
                        pincode: ""
                    }));
                }
            } catch (error) {
                toast.warning("Failed to load user data");
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [id, reset, setLoading]);

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setImageError("File size must be less than 5MB");
            return;
        }
        if (!["image/jpeg", "image/jpg", "image/png", "image/gif"].includes(file.type)) {
            setImageError("Only JPG, PNG, or GIF files are allowed");
            return;
        }

        setImageError("");
        setProfileImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setProfileImage(reader.result);
        reader.readAsDataURL(file);
    };

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const formDataSubmit = new FormData();
            formDataSubmit.append("first_name", data.firstName);
            formDataSubmit.append("last_name", data.lastName);
            formDataSubmit.append("contact_email", data.email);
            formDataSubmit.append("mobile", data.mobile);
            formDataSubmit.append("address", data.address || "");
            formDataSubmit.append("country", formData.country === "IN" ? "India" : data.country || "");
            formDataSubmit.append("state", formData.state || "");
            formDataSubmit.append("district", formData.district || "");
            formDataSubmit.append("city", formData.city || "");
            formDataSubmit.append("pincode", formData.pincode || "");

            if (profileImageFile) {
                formDataSubmit.append("profilePicture", profileImageFile);
            }

            const res = await updatUser(id, formDataSubmit);
            if (res.data.success) {
                toast.success(res.data.message || "User updated successfully");
                navigate(`${process.env.PUBLIC_URL}/user`);
            }
        } catch (error) {
            console.error(error);
            toast.warning("Failed to update user");
        } finally {
            setLoading(false);
        }
    };

    // Rest of JSX remains the same...
    return (
        <>
            <Breadcrumbs mainTitle="Update User" parent="Home" subParent="List Of User" title="Update User" />

            <Container fluid={true}>
                <Row>
                    <Col>
                        <Card className="tw-shadow-xl tw-rounded-2xl tw-border-0 tw-bg-white tw-p-8">
                            <div className="card-body">
                                <div className="row">
                                    {/* Left: Profile Image */}
                                    <div className="col-md-4 tw-flex tw-flex-col tw-items-center tw-border-r tw-border-gray-200">
                                        {/* ... same as before ... */}
                                        <div className="tw-mb-6">
                                            <div className="tw-relative tw-inline-block">
                                                <label htmlFor="profile-upload" className="tw-cursor-pointer tw-block">
                                                    <div className="tw-w-40 tw-h-40 tw-rounded-full tw-overflow-hidden tw-bg-gray-100 tw-border-4 tw-border-dashed tw-border-gray-300 tw-flex tw-items-center tw-justify-center hover:tw-border-primary tw-transition-all">
                                                        {profileImage ? (
                                                            <S3ImageLoader fileName={profileImage}
                                                                fallbackSrc={errorProfile}
                                                                className="m-0 rounded-circle border border-5 border-white shadow-lg"
                                                                alt="Patient"
                                                                style={{ width: '160px', height: '160px', objectFit: 'cover' }}
                                                            />
                                                            // <img src={profileImage} alt="Profile" className="tw-w-full tw-h-full tw-object-cover" />
                                                        ) : (
                                                            <div className="tw-text-center">
                                                                <Globe className="tw-w-16 tw-h-16 tw-text-gray-400" />
                                                                <p className="tw-text-gray-500 tw-text-sm tw-mt-2">Click to upload</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </label>
                                                <label htmlFor="profile-upload" className="tw-absolute tw-bottom-2 tw-right-2 bg-primary tw-rounded-full tw-p-2 tw-shadow-md tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-border-2 tw-border-white">
                                                    <Pencil className="tw-w-4 tw-h-4 tw-text-white" />
                                                </label>
                                                <input
                                                    id="profile-upload"
                                                    type="file"
                                                    accept="image/jpeg,image/jpg,image/png,image/gif"
                                                    onChange={handleImageUpload}
                                                    className="tw-hidden"
                                                />
                                            </div>
                                            {imageError && (
                                                <div className="tw-flex tw-items-center tw-gap-2 tw-text-red-600 tw-text-sm tw-mt-3">
                                                    <AlertCircle className="tw-w-4 tw-h-4" />
                                                    <span>{imageError}</span>
                                                </div>
                                            )}
                                            <p className="tw-text-gray-500 tw-text-xs tw-mt-2">Upload Profile Image</p>
                                        </div>

                                        <div className="tw-w-full tw-rounded-xl tw-p-4 tw-mt-6" style={{ border: "1px solid #1b255b" }}>
                                            <h6 className="tw-font-semibold tw-text-gray-700 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                                <Building2 className="tw-w-5 tw-h-5" />
                                                Laboratory Info
                                            </h6>
                                            <div className="tw-space-y-3 tw-text-sm">
                                                <div className="tw-flex tw-justify-between">
                                                    <span className="tw-text-gray-600">Lab Name:</span>
                                                    <span className="tw-font-medium">{currentLab?.facility_name}</span>
                                                </div>
                                                <div className="tw-flex tw-justify-between">
                                                    <span className="tw-text-gray-600">Base Currency:</span>
                                                    <span className="tw-font-bold tw-text-primary">{currentLab?.baseCurrency || "INR"}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right: Form */}
                                    <div className="col-md-8 tw-ps-5">
                                        <form onSubmit={handleSubmit(onSubmit)} className="tw-space-y-5">
                                            {/* Name, Email, Mobile - same as before */}
                                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                                <div>
                                                    <label className="tw-block tw-font-medium tw-mb-1">First Name <span className="tw-text-red-500">*</span></label>
                                                    <input type="text" className="form-control" {...register("firstName", { required: "First name is required" })} />
                                                    {errors.firstName && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.firstName.message}</p>}
                                                </div>
                                                <div>
                                                    <label className="tw-block tw-font-medium tw-mb-1">Last Name <span className="tw-text-red-500">*</span></label>
                                                    <input type="text" className="form-control" {...register("lastName", { required: "Last name is required" })} />
                                                    {errors.lastName && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.lastName.message}</p>}
                                                </div>
                                            </div>

                                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                                <div>
                                                    <label className="tw-block tw-font-medium tw-mb-1">Email <span className="tw-text-red-500">*</span></label>
                                                    <input type="email" className="form-control" {...register("email", { required: "Email is required" })} readOnly />
                                                    {errors.email && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.email.message}</p>}
                                                </div>
                                                <div>
                                                    <label className="tw-block tw-font-medium tw-mb-1">Mobile Number <span className="tw-text-red-500">*</span></label>
                                                    <Controller
                                                        name="mobile"
                                                        control={control}
                                                        rules={{ required: "Mobile is required" }}
                                                        render={({ field }) => (
                                                            <PhoneInput
                                                                {...field}
                                                                country="in"
                                                                enableSearch
                                                                inputClass="form-control"
                                                                inputStyle={{ width: "100%" }}
                                                                onChange={(value) => field.onChange(value)}
                                                            />
                                                        )}
                                                    />
                                                    {errors.mobile && <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.mobile.message}</p>}
                                                </div>
                                            </div>



                                            {formData.country === "OTHER" && (
                                                <div>
                                                    <label className="tw-block tw-font-medium tw-mb-1">Country Name</label>
                                                    <input type="text" className="form-control" {...register("country")} />
                                                </div>
                                            )}




                                            <div className="tw-flex tw-justify-end tw-gap-3 tw-mt-8">
                                                <button type="submit" disabled={isSubmitting} className="btn btn-primary tw-px-8">
                                                    {isSubmitting ? "Updating..." : "Update User"}
                                                </button>
                                                <Link to={`${process.env.PUBLIC_URL}/user`}>
                                                    <button type="button" className="btn btn-secondary">Cancel</button>
                                                </Link>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}